import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Navbar } from './Navbar';
import { RouteSeo } from './RouteSeo';
import { ThemeProvider } from '@/app/providers/ThemeProvider';
import { APP_VERSION_LABEL } from '@/shared/config/app-version';

import { getLayoutContainerClass } from '@/shared/config/layout';

const CommandPalette = lazy(() =>
  import('./CommandPalette').then(({ CommandPalette: Component }) => ({ default: Component })),
);
const Drawer = lazy(() =>
  import('@/shared/ui/Drawer').then(({ Drawer: Component }) => ({ default: Component })),
);
const ToastContainer = lazy(() =>
  import('@/shared/ui/ToastContainer').then(({ ToastContainer: Component }) => ({
    default: Component,
  })),
);

function DeferredToastContainer() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    let idleId: number | undefined;
    let fallbackId: number | undefined;

    const mountToastContainer = () => {
      if (fallbackId !== undefined) window.clearTimeout(fallbackId);
      setIsMounted(true);
    };
    const scheduleMount = () => {
      fallbackId = window.setTimeout(mountToastContainer, 2000);
      if (window.requestIdleCallback) {
        idleId = window.requestIdleCallback(mountToastContainer, { timeout: 2000 });
      }
    };

    if (document.readyState === 'complete') scheduleMount();
    else window.addEventListener('load', scheduleMount, { once: true });

    return () => {
      window.removeEventListener('load', scheduleMount);
      if (fallbackId !== undefined) window.clearTimeout(fallbackId);
      if (idleId !== undefined) window.cancelIdleCallback?.(idleId);
    };
  }, []);

  if (!isMounted) return null;
  return (
    <Suspense fallback={null}>
      <ToastContainer />
    </Suspense>
  );
}

export function Layout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasOpenedMobileMenu, setHasOpenedMobileMenu] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [hasOpenedCommandPalette, setHasOpenedCommandPalette] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  useEffect(() => {
    const handleSearchShortcut = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'k' || (!event.metaKey && !event.ctrlKey)) return;
      event.preventDefault();
      setHasOpenedCommandPalette(true);
      setIsCommandPaletteOpen((open) => !open);
    };

    document.addEventListener('keydown', handleSearchShortcut);
    return () => document.removeEventListener('keydown', handleSearchShortcut);
  }, []);

  const openMobileMenu = () => {
    setHasOpenedMobileMenu(true);
    setIsMobileMenuOpen(true);
  };

  const openCommandPalette = () => {
    setHasOpenedCommandPalette(true);
    setIsCommandPaletteOpen(true);
  };

  const containerClass = getLayoutContainerClass(location.pathname);

  return (
    <div className="flex min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <RouteSeo />
      <Sidebar className="hidden md:block" />

      <div className="flex-1 flex flex-col min-w-0">
        <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
          <Navbar onMenuClick={openMobileMenu} onSearchClick={openCommandPalette} />
        </ThemeProvider>

        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 p-4 sm:p-6 overflow-y-auto overflow-x-hidden"
        >
          <div className={`mx-auto ${containerClass}`}>
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </main>

        <footer className="border-t px-4 sm:px-6 py-5 text-sm text-muted-foreground">
          <div
            className={`mx-auto flex ${containerClass} flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}
          >
            <p>
              © 2026 Oxbit Labs. EasyUtils{' '}
              <Link className="hover:text-foreground" to="/changelog">
                {APP_VERSION_LABEL}
              </Link>
              .
            </p>
            <nav className="flex flex-wrap gap-x-4 gap-y-2" aria-label="Company links">
              <Link className="hover:text-foreground" to="/about">
                About
              </Link>
              <Link className="hover:text-foreground" to="/privacy">
                Privacy
              </Link>
              <Link className="hover:text-foreground" to="/terms">
                Terms
              </Link>
              <Link className="hover:text-foreground" to="/changelog">
                Changelog
              </Link>
              <a className="hover:text-foreground" href="/llms.txt">
                AI index
              </a>
            </nav>
          </div>
        </footer>
      </div>

      {hasOpenedCommandPalette && (
        <Suspense fallback={null}>
          <CommandPalette open={isCommandPaletteOpen} onOpenChange={setIsCommandPaletteOpen} />
        </Suspense>
      )}

      {hasOpenedMobileMenu && (
        <Suspense fallback={null}>
          <Drawer
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
            position="left"
            className="p-0 w-64"
          >
            <Sidebar
              className="w-full border-none h-full"
              onNavigate={() => setIsMobileMenuOpen(false)}
            />
          </Drawer>
        </Suspense>
      )}
      <DeferredToastContainer />
    </div>
  );
}
